// @ts-strict-ignore
import * as React from 'react';
import { releaseStepDetailsContainerModuleStyle, antGrey, titleStyle } from '../styles/globalStyles';
import { TeamOutlined } from '@ant-design/icons';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import BetaTestersDialog from './BetaTestersDialog';
import {
    AutomationType,
    IntegrationType,
    BetaTestingGroup,
    BetaTester,
    AutomationAssignBetaBuildGroups,
} from '@/types';
import BetaGroupsDialog from './BetaGroupsDialog';
import { betaIntegrationTypeSupportsIndividualTesters, isTestFlightBetaIntegration } from '@/helpers';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '@/typescriptHelpers';
import { selectApp } from '@/selectors/selectors';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import AutomatedByRunwayIcon from '../AutomatedByRunwayIcon';

interface BetaGroupsModuleProps extends ReleaseComponentProps {
    allTesters: BetaTester[];
    allGroups: BetaTestingGroup[];
    selectedTesters: string[];
    selectedGroups: string[];
    externalBuildId: string;
    betaIntegrationType: IntegrationType;
    editGroupsDisabled: boolean;
    shouldHideEditGroupsButton?: boolean;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    autoAssignBetaGroupsEnabled: boolean;
}

const BetaGroupsModule: React.FC<BetaGroupsModuleProps> = (props: BetaGroupsModuleProps): JSX.Element => {
    const titleTextStyle: React.CSSProperties = {
        ...titleStyle,
        color: antGrey.grey7,
        fontSize: 12,
    };

    const [betaTestersDialogVisible, setBetaTestersDialogVisible] = React.useState(false);
    const [betaGroupsDialogVisible, setBetaGroupsDialogVisible] = React.useState(false);

    const getGroupNameById = (id: string): string => {
        return props.allGroups.find((group) => group.id === id).name;
    };

    const defaultExternalGroups = useSelector((state: RootState) => {
        const autoAssignBetaGroupIdsAutomation = selectApp(state, props.appId)?.appSettings.automations.find(
            (value) => value.type === AutomationType.AutoAssignBetaBuilds,
        ) as AutomationAssignBetaBuildGroups | undefined;
        return autoAssignBetaGroupIdsAutomation?.data?.selectedBetaGroupIds ?? [];
    });

    const supportsIndividualTesters = betaIntegrationTypeSupportsIndividualTesters(props.betaIntegrationType);

    const getText = () => {
        switch (props.betaIntegrationType) {
            case IntegrationType.TestFlight:
            case IntegrationType.FirebaseBeta:
                return { titleText: 'Groups', ctaText: 'Edit groups' };
            case IntegrationType.GoogleBeta:
                return { titleText: 'Google groups', ctaText: 'Select Google groups' };
            default:
                return { titleText: 'Testing groups', ctaText: 'Edit testing groups' };
        }
    };

    const { titleText, ctaText } = getText();

    const canAutoAssignBetaGroups = isTestFlightBetaIntegration(props.betaIntegrationType);

    const emptyTestingGroups = (integrationType: IntegrationType) => {
        let text = '';
        if (integrationType === IntegrationType.FirebaseBeta) {
            text = 'Due to an API limitation, Firebase App Distribution testing groups cannot be viewed in Runway.';
        } else {
            text = 'No testing groups';
        }

        return <Text type="secondary">{text}</Text>;
    };

    return (
        <div style={{ ...releaseStepDetailsContainerModuleStyle, backgroundColor: antGrey.grey2, overflow: 'hidden' }}>
            <div style={supportsIndividualTesters ? { display: 'grid', gridTemplateColumns: '47% 6% 47%' } : {}}>
                <Space direction="vertical" size={12} style={!supportsIndividualTesters && { width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text style={{ ...titleTextStyle }}>{titleText}</Text>
                        {!props.shouldHideEditGroupsButton && (
                            <PermissionedActionButton
                                releaseId={props.releaseId}
                                actionType={RunwayUserAction.UAAssignBetaGroupsToBuilds}
                                type="link"
                                icon={<TeamOutlined />}
                                style={{ lineHeight: 1.5715 }}
                                onClick={() => setBetaGroupsDialogVisible(true)}
                                disabled={props.editGroupsDisabled || !props.betaIntegrationType}
                            >
                                {ctaText}
                            </PermissionedActionButton>
                        )}
                    </div>
                    <div>
                        <ul style={{ paddingLeft: 24, paddingTop: 0, paddingBottom: 0, paddingRight: 0 }}>
                            {props.selectedGroups?.map((group, index) => (
                                <li key={index + group}>
                                    <Space>
                                        {canAutoAssignBetaGroups &&
                                            props.autoAssignBetaGroupsEnabled &&
                                            defaultExternalGroups &&
                                            defaultExternalGroups.includes(group) && <AutomatedByRunwayIcon />}
                                        <Text>{getGroupNameById(group)}</Text>
                                    </Space>
                                </li>
                            ))}
                        </ul>
                        {props.selectedGroups.length === 0 && emptyTestingGroups(props.betaIntegrationType)}
                    </div>
                </Space>
                {supportsIndividualTesters && (
                    <>
                        <Divider
                            type="vertical"
                            style={{ height: '100%', backgroundColor: antGrey.grey5, width: 1, marginLeft: '50%' }}
                        />
                        <Space direction="vertical" size={12}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text style={{ ...titleTextStyle }}>Individual testers</Text>
                                {/* <Button
                                    type="link"
                                    icon={<TeamOutlined />}
                                    style={{ lineHeight: 1.5715 }}
                                    onClick={() => setBetaTestersDialogVisible(true)}
                                >
                                    Edit testers
                                </Button> */}
                            </div>
                            {props.selectedTesters?.length > 0 ?
                                <ul style={{ paddingLeft: 24, paddingTop: 0, paddingBottom: 0, paddingRight: 0 }}>
                                    {props.selectedTesters?.map((tester, index) => (
                                        <li key={index + tester}>{tester}</li>
                                    ))}
                                </ul>
                            :   <Text type="secondary">No individual testers</Text>}
                        </Space>
                    </>
                )}
            </div>
            <BetaTestersDialog
                orgId={props.orgId}
                open={betaTestersDialogVisible}
                betaIntegrationType={props.betaIntegrationType}
                onCancel={() => setBetaTestersDialogVisible(false)}
                betaTesters={{ selected: props.selectedTesters, all: props.allTesters }}
                appId={props.appId}
                externalBuildId={props.externalBuildId}
                stepRunComponentProps={props.stepRunComponentProps}
                releaseId={props.releaseId}
                onSaveSuccess={() => setBetaTestersDialogVisible(false)}
            />
            <BetaGroupsDialog
                orgId={props.orgId}
                appId={props.appId}
                stepRunComponentProps={props.stepRunComponentProps}
                externalBuildId={props.externalBuildId}
                onSaveSuccess={() => setBetaGroupsDialogVisible(false)}
                releaseId={props.releaseId}
                open={betaGroupsDialogVisible}
                onCancel={() => setBetaGroupsDialogVisible(false)}
                betaGroups={{ selected: props.selectedGroups, all: props.allGroups }}
                betaIntegrationType={props.betaIntegrationType}
            />
        </div>
    );
};

export default BetaGroupsModule;
